/**
 * Adressen til API-et kan komme to steder fra:
 *
 *  1. `window.__BONTO__`, satt av /config.js. Den fila skrives av containeren
 *     ved oppstart, slik at samme docker-image kan kjøre på et hvilket som
 *     helst domene uten å bygges på nytt.
 *  2. `.env` ved bygging (VITE_*). Praktisk under lokal utvikling.
 *
 * Uten noen av delene starter appen i demomodus.
 */
// __LOMMEPENGER__ er navnet globalen hadde før appen ble Bonto. Service
// workeren har config.js i cache som reserve, og en nettleser uten nett rett
// etter en utrulling kan servere den gamle fila til den nye appen. Uten dette
// fallbacket ville appen ikke funnet API-adressen, og falt tilbake til
// demomodus med oppdiktede tall. Kan fjernes når ingen cache er så gammel.
const runtime = typeof window !== 'undefined'
  ? (window.__BONTO__ ?? window.__LOMMEPENGER__ ?? {})
  : {}

// Tomme strenger og malverdien fra config.js-malen teller ikke som satt.
const clean = (value) => {
  const text = String(value ?? '').trim()
  return text && !text.startsWith('__') ? text : ''
}

const runtimeUrl = clean(runtime.supabaseUrl)
const runtimeKey = clean(runtime.supabaseAnonKey)

export const SUPABASE_ANON_KEY =
  runtimeKey || clean(import.meta.env.VITE_SUPABASE_ANON_KEY)

export const SUPABASE_URL =
  runtimeUrl ||
  // I self-hosted oppsett ligger API-et bak samme nginx som appen. Da holder
  // det å bruke adressen siden allerede kjører på - og appen fungerer likt på
  // https://domenet.no og http://192.168.x.x:8090 uten å bygges på nytt.
  (runtimeKey ? window.location.origin : '') ||
  clean(import.meta.env.VITE_SUPABASE_URL)

export const hasSupabaseConfig = Boolean(SUPABASE_URL && SUPABASE_ANON_KEY)

/**
 * ID-en Umami kjenner nettstedet på. Settes av /config.js, altså av
 * containeren ved oppstart - den lages i Umami første gang og finnes ikke ved
 * bygging. Står den tom, lastes ikke sporingsskriptet i det hele tatt.
 */
export const UMAMI_WEBSITE_ID = clean(runtime.umamiWebsiteId)

/**
 * Demomodus kjører hele appen mot et datasett i nettleseren - ingen database,
 * ingen konto. Slås på automatisk når API-et ikke er satt opp ennå, eller
 * manuelt med ?demo i adressen. ?demo=0 slår den av igjen.
 *
 * Valget huskes i sessionStorage, altså for denne fanen og til den lukkes.
 * Uten det ville demoen falt ut ved første oppfriskning: lenkene inne i appen
 * bytter side uten å ta med spørringen, så adressen mister ?demo med én gang
 * man klikker seg videre.
 */
const DEMO_KEY = 'bonto.demo'

function demoRequested() {
  const param = new URLSearchParams(location.search).get('demo')
  const asked = param !== null && !['0', 'false', 'av'].includes(param)
  try {
    if (param !== null) {
      if (asked) sessionStorage.setItem(DEMO_KEY, '1')
      else sessionStorage.removeItem(DEMO_KEY)
    }
    return asked || sessionStorage.getItem(DEMO_KEY) === '1'
  } catch {
    return asked
  }
}

export const isDemo = !hasSupabaseConfig || demoRequested()
